import { Box, Flex } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { ContentDetailModal } from '~/components/ContentDetailModal';
import DocumentTitle from '~/components/DocumentTitle';
import { useGetAgency } from '~/hooks/api/agencies/get';
import { MediaKit } from '~/hooks/api/media-kits/get';
import { useFetchCreatorPublic } from '~/hooks/api/media-kits/useFetchCreatorPublic';
import { getShareTokenByCreatorSlug } from '~/lib/getShareToken';
import { ComponentRenderer } from '~/screens/PageBuilder/canvas/ComponentRenderer';

import {
  AVAILABLE_FONTS,
  getDefaultMKColors,
} from '../AgencySettingsScreen/components/GeneralTab/components/AgencyBranding/constants';
import {
  applyMediaKitFont,
  getMediaKitColors,
} from '../AgencySettingsScreen/components/GeneralTab/components/AgencyBranding/utils';
import { useAddContentStore } from '../PageBuilder/sidebar/useAddContentStore';
import { TopBar } from './Topbar';

export const MediaKitPreview = ({ data }: { data: MediaKit }) => {
  const agency = useGetAgency(data.agency_id);

  const { isContentDetailModalOpen, contentId, setIsContentDetailModalOpen } =
    useAddContentStore();

  const mkCreatorId = useMemo(() => {
    const id = data.blocks.find((block) => block.component === 'TalentProfile')
      ?.properties?.creator?.id;
    return id;
  }, [data]);

  const creator = useFetchCreatorPublic(mkCreatorId as string);

  useEffect(() => {
    // Runs even without an agency: creator-owned kits have no agency to theme
    // from, and skipping here left them on the stylesheet's stale palette.
    const brandPrimaryColor = agency.data?.theming?.primaryColor || '';
    const brandSecondaryColor = agency.data?.theming?.secondaryColor || '';
    const brandBackgroundColor =
      agency.data?.theming?.backgroundColor || '#fff';
    const brandFont = agency.data?.theming?.font;

    const mkColors = getMediaKitColors({
      primaryColor: brandPrimaryColor,
      secondaryColor: brandSecondaryColor,
      backgroundColor: brandBackgroundColor,
    });
    Object.entries(mkColors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value as string);
    });

    if (brandBackgroundColor) {
      // update meta theme color
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', brandBackgroundColor);

      // Update document background color to prevent awkward white flash while scrolling
      document.body.style.backgroundColor =
        mkColors['--media-kit-background-primary'];
    }

    // Load font if specified in media kit data
    const font = AVAILABLE_FONTS.find((f) => f.name === brandFont);
    applyMediaKitFont(font);

    // Cleanup: reset CSS variables to defaults when component unmounts
    return () => {
      const defaultColors = getDefaultMKColors();
      Object.entries(defaultColors).forEach(([key, value]) => {
        document.documentElement.style.setProperty(key, value);
      });
      document.body.style.backgroundColor = '';
    };
  }, [agency.data]);

  return (
    <>
      <DocumentTitle>{data.name}</DocumentTitle>

      <Box
        as="main"
        maxWidth={1200}
        marginX="auto"
        paddingX={{
          base: 6,
          lg: 0,
        }}
        style={{ fontFamily: 'var(--media-kit-font-family, inherit)' }}
      >
        <TopBar mediaKit={data} />
        <Flex
          flexDirection="column"
          gap={10}
          color="var(--media-kit-text-base)"
        >
          {data.blocks.map((block) => (
            <ComponentRenderer block={block} key={block.id} readonly />
          ))}
        </Flex>

        <ContentDetailModal
          isOpen={isContentDetailModalOpen}
          onClose={() => setIsContentDetailModalOpen(false, undefined)}
          id={contentId}
          shareToken={getShareTokenByCreatorSlug(creator.data?.slug ?? '')}
        />
      </Box>
    </>
  );
};
